// src/components/settings/PayrollRatesPage.js
import React, { useState, useEffect, useCallback } from "react";
import Icon from "../icons/Icon";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { PHILIPPINE_LABOR_LAW_MINIMUMS } from "../../constants/laborLawMinimums";
import {
  validateCutoffStartDays,
  officialPeriods,
  DEFAULT_CUTOFF_START_DAYS,
} from "../../utils/payCutoffs";
import { toYMDString } from "../../utils/periodDate";
import { fetchPayrollIndex } from "../../services/payrollIndexService";
import { formatPeso } from "../../utils/currency";
import { SSS_BASIS_FACTORS } from "../../constants/sssBasisFactors";
import ContributionBasisCard from "./ContributionBasisCard";
import ThirteenthMonthBasisCard from "./ThirteenthMonthBasisCard";
import {
  DEFAULT_THIRTEENTH_MONTH_BASIS,
  normaliseThirteenthMonthBasis,
} from "../../constants/thirteenthMonthBasis";
import "../../styles/components/settings-pages.css";

// Default rates (Philippine Labor Law minimums)
const DEFAULT_RATES = {
  overtimeRate: 1.25,
  nightDiffRate: 0.10,
  regularHolidayRate: 2.0,
  specialHolidayRate: 1.3,
  restDayRate: 1.3,
  restDayOvertimeRate: 1.69,
  // hoursPerDay moved to Settings > Attendance Policy (PRD_MonthlySalaryType C4).
  // workingDaysPerMonth deleted (C5 — never read by any calculation);
  // dailyRateFactor (C1) is the sole monthly→daily divisor.
  dailyRateFactor: 365,
  // Payroll approval policy (PRD_PayrollApproval.md §Approval Policy)
  // 'self-approval' (default): anyone with payroll:approve may approve, including submitter.
  // 'maker-checker': submitter cannot approve their own run; another approver required.
  payrollApprovalType: 'self-approval',
  // CBA premium base (PRD_CbaPremiumBase C19). Every value here reproduces
  // today's arithmetic: with all three bases false, the engine's
  // `cbaOn && bases.X` is false at all three read sites and every premium keeps
  // using the employee's own hourly rate. `minimumDailyWage: 0` — NEVER
  // undefined: handleSave persists this whole merged object to every company on
  // its next save, and this project does not set ignoreUndefinedProperties, so
  // an undefined would throw and break Save for the entire page.
  minimumDailyWage: 0,
  cbaPremiumBases: { overtime: false, nightDiff: false, undertime: false },
  // PRD_ShiftRules Amendment B: false = night pay on OT hours on the day rate (the
  // default); true = on the OT rate (DOLE). A literal, not absent: the no-doc path
  // seeds rates/initialRates from this object raw, and hasChanges' generic `!==`
  // needs a defined baseline or option 2 → option 1 leaves the save bar stuck on.
  nightDiffOnOtRate: false,
  // SSS contribution basis (PRD_SssContributionBasis C11/C19). `enabled: false`
  // is today's exact behaviour — nothing reads `factors` while it is off. All
  // seven factors default true (C4) so enabling needs one flip. Every FACTOR
  // value a literal boolean — same undefined-throws-Save hazard as minimumDailyWage.
  // Mirrors DEFAULT_SSS_BASIS in functions/src/payroll/constants.js.
  sssBasis: {
    enabled: false,
    factors: {
      basic: true, overtime: true, nightDiff: true, holiday: true, restDay: true,
      undertime: true, absence: true,
    },
    // PRD_SssBasisPieceRateAndSuppressionReasons P4: 'piece' (today's
    // behaviour) or 'daily'. Outside `factors` — that map is rebuilt from its
    // key set with every value coerced to a boolean.
    // This entry documents the shape; it is NOT the effective default, and no
    // test asserts it (impl-review r2, edge finding — known, left as is).
    // normaliseRates runs on every load and hardcodes its own 'piece' fallback
    // rather than reading this. Change one and the other does not follow, and
    // nothing in CI will say so — so change both together.
    pieceRateValuation: "piece",
  },
  // H2a: same key SET as sssBasis, different DEFAULTS — `undertime` and
  // `absence` start FALSE. They are the two factors PhilHealth Circular
  // 2018-0001 names, so enabling the basis should not in one click also produce
  // the configuration that circular most directly contradicts. Mirrors
  // DEFAULT_PHILHEALTH_BASIS on the server.
  philhealthBasis: {
    enabled: false,
    factors: {
      basic: true, overtime: true, nightDiff: true, holiday: true, restDay: true,
      undertime: false, absence: false,
    },
    pieceRateValuation: "piece",
  },
  // PRD_ThirteenthMonthPay C4 — literal booleans for the same
  // undefined-throws-Save reason as above.
  thirteenthMonthBasis: DEFAULT_THIRTEENTH_MONTH_BASIS,
};

// C19: the stored doc is spread SHALLOWLY over DEFAULT_RATES, so a stored
// `sssBasis` would replace the default wholesale and any factor key absent
// from the stored map would read undefined → falsy → silently unticked. Rebuild
// it here, at the point of use: missing key → its default (true), present key →
// a literal boolean, unknown keys dropped. Same rule as cbaPremiumBases on the
// server side (computePayrollEligibility.js), which is why neither reader is
// deep-merged.
const normaliseRates = (data) => {
  const merged = { ...DEFAULT_RATES, ...data };
  const stored = merged.sssBasis;
  merged.sssBasis = {
    enabled: stored?.enabled === true,
    factors: Object.fromEntries(
      Object.keys(DEFAULT_RATES.sssBasis.factors).map((k) => [
        k,
        // Basic pay is never a choice (C2): always true, whatever is stored.
        k === "basic"
          ? true
          : stored?.factors?.[k] === undefined
            ? DEFAULT_RATES.sssBasis.factors[k]
            : Boolean(stored.factors[k]),
      ])
    ),
    // P4: anything but the literal 'daily' — absent, junk, 'piece' — is 'piece'.
    // Mirrors normaliseSssBasis on the server.
    pieceRateValuation: stored?.pieceRateValuation === "daily" ? "daily" : "piece",
  };

  // H2a: the identical rule against PhilHealth's OWN defaults. A separate block
  // rather than a loop over both, so a future edit to one agency's rule cannot
  // silently change the other's.
  const phStored = merged.philhealthBasis;
  merged.philhealthBasis = {
    enabled: phStored?.enabled === true,
    factors: Object.fromEntries(
      Object.keys(DEFAULT_RATES.philhealthBasis.factors).map((k) => [
        k,
        k === "basic"
          ? true
          : phStored?.factors?.[k] === undefined
            ? DEFAULT_RATES.philhealthBasis.factors[k]
            : Boolean(phStored.factors[k]),
      ])
    ),
    pieceRateValuation: phStored?.pieceRateValuation === "daily" ? "daily" : "piece",
  };

  // PRD_ThirteenthMonthPay C4: the generator reads the same shared normaliser.
  merged.thirteenthMonthBasis = normaliseThirteenthMonthBasis(merged.thirteenthMonthBasis);
  // PRD_ShiftRules B1: the engine reads only the literal true as on.
  merged.nightDiffOnOtRate = merged.nightDiffOnOtRate === true;
  return merged;
};

// PRD_ShiftRules Amendment B3 — the night-pay-on-OT labels, computed from the form's
// rates exactly as the engine prices them (otRates[dayType] ÷ ndDayRates[dayType]).
// Each rate resolves with the engine's `|| default`, so a field cleared mid-edit shows
// the default's figure. Rounded to one decimal BEFORE comparing or printing: in
// floating point 1.3 − 1 and 1.69 ÷ 1.3 − 1 differ in the 16th place.
const round1 = (x) => Math.round(x * 1000) / 10;
const pctText = (p) => `${p}%`;
const nightOtLabels = (rates) => {
  const rate = (key) => Number(rates[key]) || DEFAULT_RATES[key];
  const restDay = rate("restDayRate");
  const ordinaryPct = round1(rate("overtimeRate") - 1);
  const restDayPct = round1(Math.max(rate("restDayOvertimeRate"), restDay * restDay) / restDay - 1);
  const holidayPct = round1(restDay - 1);
  const rest = restDayPct === holidayPct
    ? `+${pctText(restDayPct)} on rest days and holidays`
    : `+${pctText(restDayPct)} on rest days, +${pctText(holidayPct)} on holidays`;
  return {
    plain: `${pctText(round1(rate("nightDiffRate")))} of the day's rate`,
    withOt: `+${pctText(ordinaryPct)} on ordinary days, ${rest}`,
  };
};

// Rate field configuration
const RATE_FIELDS = [
  {
    key: "overtimeRate",
    label: "Regular Overtime",
    suffix: "x",
    description: "Multiplier for work beyond 8 hours",
    step: 0.01,
  },
  {
    key: "nightDiffRate",
    label: "Night Differential",
    suffix: "+",
    description: "Additional rate for work 10PM-6AM",
    step: 0.01,
  },
  {
    key: "regularHolidayRate",
    label: "Regular Holiday",
    suffix: "x",
    description: "Rate for regular holidays",
    step: 0.1,
  },
  {
    key: "specialHolidayRate",
    label: "Special Holiday",
    suffix: "x",
    description: "Rate for special non-working holidays",
    step: 0.1,
  },
  {
    key: "restDayRate",
    label: "Rest Day",
    suffix: "x",
    description: "Rate for work on rest days",
    step: 0.1,
  },
  {
    key: "restDayOvertimeRate",
    label: "Rest Day Overtime",
    suffix: "x",
    description: "OT rate on rest days",
    step: 0.01,
  },
];


const PayrollRatesPage = () => {
  const { company, hasPermission } = useAuth();
  const canEdit = hasPermission("set:rates:edit");
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [initialRates, setInitialRates] = useState(DEFAULT_RATES);
  // C1: "Custom" stays selected while the stored factor equals a preset value.
  const [factorCustom, setFactorCustom] = useState(false);
  // Editable value shown in the Custom factor card (independent of which preset
  // is active); defaults to a sensible non-preset divisor.
  const [customFactor, setCustomFactor] = useState(261);
  // C2/C9 — pay-schedule cutoffs (settings/payroll doc, separate from payrollRates).
  const [cutoffDays, setCutoffDays] = useState(DEFAULT_CUTOFF_START_DAYS);
  const [initialCutoffDays, setInitialCutoffDays] = useState(DEFAULT_CUTOFF_START_DAYS);
  const [cutoffError, setCutoffError] = useState(null);
  // C9: cutoff edits are blocked while a draft/pending-approval run exists.
  const [cutoffLockedBy, setCutoffLockedBy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  // --- Minimum wage (PRD_CbaPremiumBase Amendment A) ---
  //
  // The re-key confirmation. UI-ONLY — never written to Firestore, so it is a
  // separate useState and NOT a member of `rates`: handleSave persists
  // `{...rates}` wholesale, which is the same mechanism that permanently
  // re-persists the orphaned region/sector fields. Seeded from the stored wage
  // on load, on Cancel, and after a successful save, so simply opening the page
  // never blocks on an empty box.
  const [confirmWage, setConfirmWage] = useState("");
  // The wage error is DELIBERATELY not in `validationErrors`: that object drives
  // the Premium Pay Rates card's "Below Minimum" badge
  // (isAllCompliant = Object.keys(validationErrors).length === 0), so a wage
  // error there would flag six compliant multipliers as non-compliant. It is
  // also rebuilt wholesale by validateRates on every rate keystroke, which would
  // silently wipe a wage error. Shape: {message} | null — there is no `blocking`
  // flag any more, because both rules block (Amendment A).
  const [wageError, setWageError] = useState(null);

  // Fetch rates from Firestore
  useEffect(() => {
    const fetchRates = async () => {
      if (!company?.id) return;

      try {
        setLoading(true);
        const ratesDoc = await getDoc(
          doc(db, "companies", company.id, "settings", "payrollRates")
        );

        if (ratesDoc.exists()) {
          const data = ratesDoc.data();
          setRates(normaliseRates(data));
          setInitialRates(normaliseRates(data));
          // Seed the re-key box from the stored wage so opening the page to
          // change something else never blocks on an empty confirm field.
          setConfirmWage(data.minimumDailyWage ? String(data.minimumDailyWage) : "");
          // A stored non-preset factor means Custom is the active choice.
          const storedFactor = data.dailyRateFactor ?? 365;
          if (![365, 313, 261].includes(storedFactor)) {
            setCustomFactor(storedFactor);
            setFactorCustom(true);
          }
        } else {
          // Use defaults if no rates exist
          setRates(DEFAULT_RATES);
          setInitialRates(DEFAULT_RATES);
          setConfirmWage("");
        }

        // C2: pay-schedule cutoffs live on the sibling settings/payroll doc.
        const payrollDoc = await getDoc(
          doc(db, "companies", company.id, "settings", "payroll")
        );
        const storedCutoffs = payrollDoc.exists()
          ? payrollDoc.data().cutoffStartDays
          : undefined;
        const cutoffs =
          Array.isArray(storedCutoffs) && storedCutoffs.length === 2
            ? storedCutoffs
            : DEFAULT_CUTOFF_START_DAYS;
        setCutoffDays(cutoffs);
        setInitialCutoffDays(cutoffs);

        // C9: block cutoff edits while a draft/pending-approval run exists —
        // re-tiling under an in-flight run invites double-pay.
        const runs = await fetchPayrollIndex(company.id);
        const blocking = runs.find(
          (r) => r.status === "draft" || r.status === "pending-approval"
        );
        setCutoffLockedBy(
          blocking
            ? `a ${blocking.status === "draft" ? "draft" : "pending-approval"} payroll run (${blocking.periodStart} – ${blocking.periodEnd})`
            : null
        );
      } catch (err) {
        console.error("Error fetching payroll rates:", err);
        setError("Failed to load payroll rates. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, [company?.id]);

  const anyCbaBase = Boolean(
    rates.cbaPremiumBases?.overtime ||
      rates.cbaPremiumBases?.nightDiff ||
      rates.cbaPremiumBases?.undertime
  );

  // Amendment A's two rules. Validation runs on LOAD, not only on edit: the
  // page's existing validationErrors is only ever recomputed inside
  // handleRateChange / handleCancel, so a stale stored wage that no user action
  // touches would sail straight through the save gate. This effect closes that.
  useEffect(() => {
    const wage = Number(rates.minimumDailyWage) || 0;
    const storedWage = Number(initialRates.minimumDailyWage) || 0;

    // RULE 1 — re-key on EDIT. Keyed off the edit, NOT off the tick: gating the
    // match on "a base is ticked" is bypassed by the most ordinary flow there
    // is — type 64.50 with nothing ticked and save, and on the next visit the
    // confirm field seeds to 64.50, the two agree, and ticking is never
    // challenged. The typo this guard exists for survives that.
    //
    // Numeric compare, so 645 / 645.00 / 645. all agree; the confirm box must
    // never be stricter than the field it is checking.
    if (wage !== storedWage && wage !== (Number(confirmWage) || 0)) {
      setWageError({
        rule: 1,
        message: "The two minimum daily wage figures don't match.",
      });
      return;
    }

    // RULE 2 — required on TICK. Rule 1 wins when both hold (see the clear-the
    // field case: wage becomes 0, so both fire), which is why this is an early
    // return above rather than two independent branches — wageError holds one
    // message.
    if (anyCbaBase && wage <= 0) {
      setWageError({
        rule: 2,
        message: "Required when a premium is priced on the minimum wage.",
      });
      return;
    }

    // Neither rule fires → no message AND no error styling. Load-bearing: the
    // input's red border keys off wageError's truthiness, so leaving a warning
    // here would paint the wage field red on every company that has never heard
    // of a CBA.
    setWageError(null);
  }, [rates.minimumDailyWage, initialRates.minimumDailyWage, confirmWage, anyCbaBase]);

  // Validate rates against minimums
  const validateRates = useCallback((currentRates) => {
    const errors = {};
    const minimums = PHILIPPINE_LABOR_LAW_MINIMUMS.rates;

    Object.keys(minimums).forEach((key) => {
      if (currentRates[key] < minimums[key]) {
        errors[key] = `Below minimum (${minimums[key]})`;
      }
    });

    return errors;
  }, []);

  // Check if there are unsaved changes
  const hasChanges = useCallback(() => {
    return (
      Object.keys(rates).some((key) => {
        // cbaPremiumBases and sssBasis are the OBJECT-valued rates, and their
        // toggles allocate a fresh one each time — a reference compare would
        // leave the "unsaved changes" bar stuck on forever after a
        // tick-then-untick.
        if (key === "cbaPremiumBases") {
          const a = rates[key] || {};
          const b = initialRates[key] || {};
          return ["overtime", "nightDiff", "undertime"].some(
            (k) => Boolean(a[k]) !== Boolean(b[k])
          );
        }
        // sssBasis is TWO levels deep (enabled + a factors map), so the cba
        // compare is not enough on its own — walk both levels.
        if (key === "sssBasis" || key === "philhealthBasis") {
          const a = rates[key] || DEFAULT_RATES[key];
          const b = initialRates[key] || DEFAULT_RATES[key];
          return (
            Boolean(a.enabled) !== Boolean(b.enabled) ||
            SSS_BASIS_FACTORS.some(
              (f) => Boolean(a.factors?.[f.key]) !== Boolean(b.factors?.[f.key])
            ) ||
            // P4: the one non-factor key. Without this clause the save bar
            // never appears for a valuation change and the option is inert.
            (a.pieceRateValuation || "piece") !== (b.pieceRateValuation || "piece")
          );
        }
        if (key === "thirteenthMonthBasis") {
          const a = rates[key] || DEFAULT_RATES[key];
          const b = initialRates[key] || DEFAULT_RATES[key];
          return SSS_BASIS_FACTORS.some(
            (f) => Boolean(a.factors?.[f.key]) !== Boolean(b.factors?.[f.key])
          );
        }
        return rates[key] !== initialRates[key];
      }) ||
      cutoffDays.join(",") !== initialCutoffDays.join(",")
    );
  }, [rates, initialRates, cutoffDays, initialCutoffDays]);

  // Check if all validations pass. Both of Amendment A's wage rules block, so
  // there is no `blocking` flag any more — any wageError freezes this one save
  // gate, which the six multipliers, the rate factor, the approval policy and
  // the pay cutoffs all share.
  //
  // Rule 2 is tick-gated, so an opted-out company is never frozen by a zero
  // wage. Rule 1 is NOT: an admin who edits the wage field must finish the
  // re-key or restore the old figure before anything else on this page saves.
  // Accepted deliberately — the block is self-inflicted, self-clearing, and
  // Discard is always reachable because a wage edit dirties `rates`.
  const isValid = useCallback(() => {
    return (
      Object.keys(validationErrors).length === 0 &&
      !cutoffError &&
      !wageError
    );
  }, [validationErrors, cutoffError, wageError]);

  // C2/C6/C7: cutoff day change + live validation.
  const handleCutoffChange = (idx, value) => {
    const next = [...cutoffDays];
    next[idx] = parseInt(value, 10) || 0;
    setCutoffDays(next);
    const result = validateCutoffStartDays(next);
    setCutoffError(result.ok ? null : result.error);
  };

  // Handle rate change
  const handleRateChange = (key, value) => {
    const numValue = parseFloat(value) || 0;
    const newRates = { ...rates, [key]: numValue };
    setRates(newRates);
    setValidationErrors(validateRates(newRates));
  };

  // --- Minimum wage handlers (Amendment A) ---

  const handleMinimumWageChange = (value) => {
    setRates({ ...rates, minimumDailyWage: parseFloat(value) || 0 });
  };

  const handleCbaBaseToggle = (key) => {
    const bases = rates.cbaPremiumBases || {};
    setRates({
      ...rates,
      cbaPremiumBases: { ...bases, [key]: !bases[key] },
    });
  };

  // --- SSS contribution basis (PRD_SssContributionBasis) ---
  // Modelled on handleCbaBaseToggle — NOT on handleSettingChange, whose
  // parseInt would coerce a boolean to 0. `key === "enabled"` flips the
  // toggle; any other key flips that factor.
  const handleSssBasisToggle = (key) => {
    const basis = rates.sssBasis || DEFAULT_RATES.sssBasis;
    setRates({
      ...rates,
      sssBasis:
        key === "enabled"
          ? { ...basis, enabled: !basis.enabled }
          : { ...basis, factors: { ...basis.factors, [key]: !basis.factors[key] } },
    });
  };

  // P1/P4: the piece-rate valuation is a string, not a factor boolean, so it
  // has its own setter and leaves `factors` untouched.
  const handlePieceRateValuation = (value) => {
    const basis = rates.sssBasis || DEFAULT_RATES.sssBasis;
    setRates({ ...rates, sssBasis: { ...basis, pieceRateValuation: value } });
  };

  // PhilHealth's own setters (Context: independent factor maps). Deliberately NOT a shared setter taking an
  // agency key — the two maps are independent by design, and one setter is one
  // edit away from writing both.
  const handlePhilhealthBasisToggle = (key) => {
    const basis = rates.philhealthBasis || DEFAULT_RATES.philhealthBasis;
    setRates({
      ...rates,
      philhealthBasis:
        key === "enabled"
          ? { ...basis, enabled: !basis.enabled }
          : { ...basis, factors: { ...basis.factors, [key]: !basis.factors[key] } },
    });
  };

  // PRD_ThirteenthMonthPay C4 — factors only; there is no Enable toggle.
  const handleThirteenthMonthBasisToggle = (key) => {
    const basis = rates.thirteenthMonthBasis || DEFAULT_RATES.thirteenthMonthBasis;
    setRates({
      ...rates,
      thirteenthMonthBasis: { ...basis, factors: { ...basis.factors, [key]: !basis.factors[key] } },
    });
  };

  const handlePhilhealthPieceRateValuation = (value) => {
    const basis = rates.philhealthBasis || DEFAULT_RATES.philhealthBasis;
    setRates({ ...rates, philhealthBasis: { ...basis, pieceRateValuation: value } });
  };

  // Handle work time settings change
  const handleSettingChange = (key, value) => {
    const numValue = parseInt(value, 10) || 0;
    setRates({ ...rates, [key]: numValue });
  };

  // Handle cancel - reset to initial values
  const handleCancel = () => {
    setRates(initialRates);
    setValidationErrors(validateRates(initialRates));
    // Re-seed the confirm box, or Discard would leave a stale figure sitting in
    // it against a restored wage.
    setConfirmWage(Number(initialRates.minimumDailyWage) ? String(initialRates.minimumDailyWage) : "");
  };

  // Handle save
  const handleSave = async () => {
    if (!company?.id || !isValid()) return;

    try {
      setSaving(true);
      await setDoc(
        doc(db, "companies", company.id, "settings", "payrollRates"),
        {
          ...rates,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      // C2: persist cutoffs to the sibling settings/payroll doc — only when
      // changed and not C9-locked (inputs are disabled while locked, but the
      // guard keeps a stale state from writing).
      if (
        !cutoffLockedBy &&
        cutoffDays.join(",") !== initialCutoffDays.join(",")
      ) {
        const check = validateCutoffStartDays(cutoffDays);
        if (!check.ok) throw new Error(check.error);
        await setDoc(
          doc(db, "companies", company.id, "settings", "payroll"),
          { cutoffStartDays: cutoffDays, updatedAt: new Date() },
          { merge: true }
        );
        setInitialCutoffDays(cutoffDays);
      }

      setInitialRates(rates);
      // Keep the visible box consistent with the new baseline. NOT a
      // block-preventer: setInitialRates above makes `wage === storedWage`, so
      // Rule 1's first clause is already false and no wageError could fire here
      // whatever confirmWage held. An earlier comment claimed otherwise.
      setConfirmWage(Number(rates.minimumDailyWage) ? String(rates.minimumDailyWage) : "");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      console.error("Error saving payroll rates:", err);
      setError("Failed to save payroll rates. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Format minimum display
  const formatMinimum = (field) => {
    const min = PHILIPPINE_LABOR_LAW_MINIMUMS.rates[field.key];
    if (!min) return null;

    if (field.suffix === "+") {
      return `Min: ${min} (+${(min * 100).toFixed(0)}%)`;
    }
    return `Min: ${min}x (${(min * 100).toFixed(0)}%)`;
  };

  // Check if all rates are compliant
  const isAllCompliant = Object.keys(validationErrors).length === 0;

  if (loading) {
    return (
      <div>
        <div className="settings-loading">
          <div className="settings-loading-spinner"></div>
          <div className="settings-loading-text">Loading payroll rates...</div>
        </div>
      </div>
    );
  }

  if (error && !rates) {
    return (
      <div>
        <div className="settings-error">
          <div className="settings-error-icon">!</div>
          <div className="settings-error-title">Error Loading Rates</div>
          <div className="settings-error-message">{error}</div>
          <button
            className="settings-btn settings-btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="settings-page-title">Payroll Rates</h1>
      <p className="settings-page-subtitle">
        Configure premium pay rates and work time settings for payroll
        calculations
      </p>

      {/* Premium Pay Rates Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon">
              <span role="img" aria-label="money">
                <Icon name="wallet" size={20} />
              </span>
            </div>
            <div>
              <h2 className="settings-card-title">Premium Pay Rates</h2>
              <p className="settings-card-description">
                Multipliers for overtime, holidays, and rest days
              </p>
            </div>
          </div>
          <div
            className={`compliance-badge ${
              isAllCompliant ? "" : "warning"
            }`}
          >
            <span>{isAllCompliant ? <Icon name="check" size={14} /> : <Icon name="alert-triangle" size={14} />}</span>
            {isAllCompliant ? "Labor Law Compliant" : "Below Minimum"}
          </div>
        </div>

        <div className="settings-card-body">
          <div className="settings-info-box">
            <span className="settings-info-box-icon"><Icon name="info" size={16} /></span>
            <span className="settings-info-box-text">
              These rates must meet or exceed Philippine Labor Law minimums.
              Changes will apply to future payroll runs only.
            </span>
          </div>

          <div className="settings-form-grid">
            {RATE_FIELDS.map((field) => (
              <div
                key={field.key}
                className={`rate-card ${
                  validationErrors[field.key] ? "error" : ""
                }`}
                onClick={(e) => {
                  const input = e.currentTarget.querySelector("input");
                  if (input) input.focus();
                }}
              >
                <div className="rate-card-header">
                  <span className="rate-card-label">{field.label}</span>
                  <div className="rate-card-badges">
                    <span className="rate-card-edit"><Icon name="pencil" size={14} /></span>
                    <span
                      className={`rate-card-check ${
                        validationErrors[field.key] ? "warning" : ""
                      }`}
                    >
                      {validationErrors[field.key] ? <Icon name="alert-triangle" size={13} /> : <Icon name="check" size={13} />}
                    </span>
                  </div>
                </div>
                <div className="rate-card-value">
                  <input
                    type="number"
                    className={`rate-card-input ${
                      validationErrors[field.key] ? "error" : ""
                    }`}
                    value={rates[field.key]}
                    onChange={(e) =>
                      handleRateChange(field.key, e.target.value)
                    }
                    step={field.step}
                    min={PHILIPPINE_LABOR_LAW_MINIMUMS.rates[field.key]}
                    disabled={!canEdit}
                    readOnly={!canEdit}
                  />
                  <span className="rate-card-suffix">{field.suffix}</span>
                </div>
                <div className="rate-card-description">{field.description}</div>
                <div
                  className={`rate-card-min ${
                    validationErrors[field.key] ? "error" : ""
                  }`}
                >
                  {validationErrors[field.key]
                    ? <><Icon name="alert-triangle" size={12} aria-hidden="true" /> Below minimum! Must be at least {PHILIPPINE_LABOR_LAW_MINIMUMS.rates[field.key]}</>
                    : formatMinimum(field)}
                </div>
              </div>
            ))}
          </div>

          {/* PRD_ShiftRules Amendment B3 — which rate the night differential on an
              overtime hour is taken from. Literal booleans via the approval-type
              pattern below, never the number-parsing change handlers: a saved
              string "true" would read as off in the engine. */}
          <div style={{ marginTop: 20 }}>
            <div className="form-label">Night differential on overtime hours</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {[
                { value: false, label: 'Night differential only', help: nightOtLabels(rates).plain },
                { value: true, label: 'Add the overtime premium (DOLE Handbook)', help: nightOtLabels(rates).withOt },
              ].map(opt => {
                const active = rates.nightDiffOnOtRate === opt.value;
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    aria-pressed={active}
                    disabled={!canEdit}
                    onClick={() => setRates({ ...rates, nightDiffOnOtRate: opt.value })}
                    style={{
                      flex: '1 1 240px',
                      textAlign: 'left',
                      padding: '12px 16px',
                      borderRadius: 8,
                      border: `2px solid ${active ? 'var(--ui-primary)' : 'var(--ui-border)'}`,
                      background: active ? 'var(--ui-primary-tint)' : 'var(--ui-surface)',
                      cursor: canEdit ? 'pointer' : 'not-allowed',
                      color: 'var(--ui-text)',
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {opt.label}
                      {opt.value === false && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ui-text-muted)', fontWeight: 400 }}>
                          Default
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ui-text-secondary)', lineHeight: 1.4 }}>
                      {opt.help}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Minimum Wage Card (PRD_CbaPremiumBase, as amended by Amendment A).
          Its own card rather than living inside Rate Calculation, which is
          explicitly the monthly→daily divisor. Holds the daily wage figure, its
          re-key confirmation, and the three checkboxes naming which premiums are
          priced on it. Region and Sector were here until Amendment A withdrew
          the platform-published floor. */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon gold">
              <span role="img" aria-label="scales">
                <Icon name="scale" size={20} />
              </span>
            </div>
            <div>
              <h2 className="settings-card-title">Minimum Wage</h2>
              <p className="settings-card-description">
                Set the company daily minimum wage
              </p>
            </div>
          </div>
        </div>

        <div className="settings-card-body">
          {/* Both figures on ONE row, side by side — a re-key is a comparison,
              and stacking them puts the pair on separate visual lines. This is
              also what gives .settings-form-row a JSX consumer again after the
              Region/Sector row it used to wrap was deleted. */}
          <div className="settings-form-row">
          <div className="settings-form-group">
            <label className="form-label" htmlFor="cba-min-wage">
              Minimum daily wage
            </label>
            <input
              id="cba-min-wage"
              type="number"
              step="0.01"
              min="0"
              className={`form-input ${wageError ? "error" : ""}`}
              placeholder="0.00"
              // Blank, not "0", when unset: a literal 0 sitting in the box is
              // something you have to select before typing, or the digits land
              // beside it (0 -> "0645"). Zero also MEANS unset here, so an
              // empty field is the more honest render.
              value={rates.minimumDailyWage || ""}
              // Select on focus so typing REPLACES. Without it the same
              // append/prepend problem applies to any saved figure, not just
              // zero — editing 645 to 695 gives "645695". Re-keying an amount
              // is always a whole-value edit.
              onFocus={(e) => e.target.select()}
              disabled={!canEdit}
              readOnly={!canEdit}
              onChange={(e) => handleMinimumWageChange(e.target.value)}
            />
            {/* The message sits under the WAGE field, not the confirm box below
                it. Rule 2's "Required…" names a field the confirm box cannot
                fix — typing there changes `confirmWage`, never
                `rates.minimumDailyWage` — so an operator would be pointed at
                the wrong one of two adjacent inputs. Rule 1 implicates both, so
                the wage field is right for it too. */}
            {wageError && (
              <span className="form-error-text">{wageError.message}</span>
            )}
          </div>

          {/* Amendment A: the re-key. Nothing in Sahod knows the statutory
              figure any more, so this is the only guard against a transcription
              slip — a ₱645 → ₱64.50 decimal that C18 cannot catch (it only
              fires at ≤ 0) and C1 forbids clamping. Entered second, compared
              numerically, never persisted. */}
          <div className="settings-form-group">
            <label className="form-label" htmlFor="cba-min-wage-confirm">
              Confirm minimum daily wage
            </label>
            <input
              id="cba-min-wage-confirm"
              type="number"
              step="0.01"
              min="0"
              className={`form-input ${wageError?.rule === 1 ? "error" : ""}`}
              placeholder="0.00"
              value={confirmWage}
              onFocus={(e) => e.target.select()}
              disabled={!canEdit}
              readOnly={!canEdit}
              onChange={(e) => setConfirmWage(e.target.value)}
            />
          </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <div className="form-label">Premium pay rates priced on the minimum wage</div>
            {[
              { key: "overtime", label: "Overtime" },
              { key: "nightDiff", label: "Night differential" },
              { key: "undertime", label: "Undertime" },
            ].map((opt) => {
              const ticked = Boolean(rates.cbaPremiumBases?.[opt.key]);
              // Amendment A: with no floor there is nothing to disable a box
              // FOR, so this reduces to the permission check and the one-way
              // wedge cannot exist. Rule 2 blocks Save, never the control — an
              // operator who ticks into a blocked state can always untick.
              const disabled = !canEdit;
              const wage = Number(rates.minimumDailyWage) || 0;
              // A ticked base with no wage must NOT print "₱0.00/day". That
              // figure is a lie twice over: C18 makes the engine fall back to
              // the employee's own rate rather than pay ₱0, and C12(b) means no
              // employee reaches the engine at all. Three states, not two —
              // dropping the middle branch re-opens that lie.
              const basis =
                ticked && wage > 0
                  ? `Minimum daily wage — ${formatPeso(wage)}/day`
                  : ticked
                    ? "Minimum wage value is required"
                    : "Employee's own daily rate (default)";
              return (
                <label
                  key={opt.key}
                  style={{
                    display: "block",
                    marginTop: 10,
                    cursor: disabled ? "default" : "pointer",
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={ticked}
                      disabled={disabled}
                      onChange={() => handleCbaBaseToggle(opt.key)}
                    />
                    <span style={{ fontWeight: 600 }}>{opt.label}</span>
                  </span>
                  <span
                    className="rate-card-label"
                    style={{ display: "block", marginLeft: 26 }}
                  >
                    {basis}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="settings-input-hint" style={{ marginTop: 12 }}>
            Applies only to daily-paid employees.
          </div>
        </div>
      </div>

      {/* Government Contributions section (PRD_PhilHealthContributionBasis H9).
          One heading, one lock banner, one mid-month warning, and one card per
          agency — the banner and the warning name no agency, so rendering them
          per card would stack two identical sentences. Pag-IBIG and Tax get no
          placeholder card: an empty slot reads as unfinished. */}
      <div className="settings-card" style={{ paddingBottom: 0, background: "transparent", border: "none", boxShadow: "none" }}>
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div>
              <h2 className="settings-card-title">Government Contributions</h2>
              <p className="settings-card-description">
                Which pay components compose the figure each agency&apos;s bracket is picked from
              </p>
            </div>
          </div>
        </div>
        <div className="settings-card-body" style={{ paddingTop: 0 }}>
          {/* C16: reuses the page's cutoffLockedBy STATE (an in-flight
              draft/pending-approval run) but not the cutoff card's banner —
              that copy says "Cutoffs are locked". Disabled inputs ONLY: the
              payrollRates write in handleSave is unconditional, and C16 accepts
              that residual in writing rather than adding a second save guard.
              H9: once for the section, covering both agencies. */}
          {cutoffLockedBy && (
            <div
              className="settings-input-hint"
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 8,
                background: "var(--ui-accent-tint)",
                color: "var(--ui-text-secondary)",
              }}
            >
              Contribution basis is locked while {cutoffLockedBy} exists —
              complete or discard it first.
            </div>
          )}

          {/* C16's mid-month warning — shown whenever the cards are editable. It
              is the mitigation for the lock's accepted gap (the lock lifts
              between runs, and is absent for an unsaved wizard session), so it
              is not optional copy. PRD_TimeRecordAndPayFixes C10 (BL-114) adds
              the third sentence: switching a basis OFF makes the next run deduct
              its own month, so the month the basis was holding is never
              collected. The first two sentences, including the over-deduction
              clause, are kept verbatim. Exact — the test pins this string. */}
          {canEdit && !cutoffLockedBy && (
            <div className="settings-input-hint">
              Changing this mid-month can double-count contributions already
              deducted, or leave an employee over-deducted if the basis falls.
              Change it at a month boundary. Turning a basis off skips one month:
              the next run deducts its own month, so the month the basis was
              still holding is never deducted. Recover it with an Other Deduction
              adjustment.
            </div>
          )}
        </div>
      </div>

      <ContributionBasisCard
        agency="SSS"
        agencyKey="sss"
        basis={rates.sssBasis}
        canEdit={canEdit}
        locked={cutoffLockedBy}
        onToggleFactor={handleSssBasisToggle}
        onPieceRateValuation={handlePieceRateValuation}
        /* C9 — the floor disclosure, so the cap on expected savings is seen at
           configuration time, not mid-payroll. Locked string. SSS only: its
           floor is a constant, PhilHealth's is a table parameter. */
        floorDisclosure="No composition can take an employee below the ₱5,000 minimum salary credit while they have any earnings for the month."
      />

      <ContributionBasisCard
        agency="PhilHealth"
        agencyKey="philhealth"
        basis={rates.philhealthBasis}
        canEdit={canEdit}
        locked={cutoffLockedBy}
        onToggleFactor={handlePhilhealthBasisToggle}
        onPieceRateValuation={handlePhilhealthPieceRateValuation}
      />

      {/* Rate Calculation Card (C1 — dailyRateFactor, the monthly→daily divisor) */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon gold">
              <span role="img" aria-label="calculator">
                <Icon name="bar-chart-3" size={20} />
              </span>
            </div>
            <div>
              <h2 className="settings-card-title">Rate Calculation</h2>
              <p className="settings-card-description">
                Monthly-to-daily rate divisor
              </p>
            </div>
          </div>
        </div>

        <div className="settings-card-body">
          {/* Hours Per Day moved to Settings > Attendance Policy (C4);
              Working Days Per Month deleted (C5). dailyRateFactor (C1) is the
              sole monthly→daily divisor. Cards mirror the Premium Pay Rates look;
              one is selected, and the Custom card holds an editable divisor. */}
          <div className="settings-form-grid-4">
            {[
              { value: 365, label: 'All days paid', desc: 'Includes rest days and holidays' },
              { value: 313, label: '6-day work week', desc: 'Rest days (52 Sundays) are unpaid' },
              { value: 261, label: '5-day work week', desc: 'Weekends (104 days) are unpaid' },
              { value: 'custom', label: 'Custom', desc: 'Enter your own annual paid-days' },
            ].map((opt) => {
              const factorValue = rates.dailyRateFactor ?? 365;
              const isCustomActive =
                factorCustom || ![365, 313, 261].includes(factorValue);
              const active =
                opt.value === 'custom'
                  ? isCustomActive
                  : !isCustomActive && factorValue === opt.value;
              const selectCustom = () => {
                if (!canEdit) return;
                setFactorCustom(true);
                handleSettingChange('dailyRateFactor', customFactor);
              };
              return (
                <div
                  key={String(opt.value)}
                  className={`rate-card rate-card--selectable ${active ? 'rate-card--selected' : ''}`}
                  onClick={() => {
                    if (!canEdit) return;
                    if (opt.value === 'custom') {
                      selectCustom();
                    } else {
                      setFactorCustom(false);
                      setRates({ ...rates, dailyRateFactor: opt.value });
                    }
                  }}
                >
                  <div className="rate-card-header">
                    <span className="rate-card-label">{opt.label}</span>
                    {active && <span className="rate-card-check"><Icon name="check" size={13} /></span>}
                  </div>
                  <div className="rate-card-value">
                    {opt.value === 'custom' ? (
                      <input
                        type="number"
                        className="rate-card-input"
                        value={customFactor}
                        onChange={(e) => {
                          setCustomFactor(e.target.value);
                          setFactorCustom(true);
                          handleSettingChange('dailyRateFactor', e.target.value);
                        }}
                        onFocus={selectCustom}
                        step="1"
                        min="1"
                        max="366"
                        disabled={!canEdit}
                        readOnly={!canEdit}
                      />
                    ) : (
                      <span className="rate-card-value-static">{opt.value}</span>
                    )}
                  </div>
                  <div className="rate-card-description">{opt.desc}</div>
                </div>
              );
            })}
          </div>
          <div className="settings-input-hint" style={{ marginTop: 12 }}>
            Daily rate for a monthly-paid employee = monthly salary × 12 ÷ this
            factor. Used only for deductions (absences, undertime) and worked
            premiums — never for the fixed base pay.
          </div>
        </div>
      </div>

      <ThirteenthMonthBasisCard
        basis={rates.thirteenthMonthBasis}
        canEdit={canEdit}
        onToggleFactor={handleThirteenthMonthBasisToggle}
      />

      {/* Pay Schedule Card (C2/C6/C7/C9 — settings/payroll.cutoffStartDays) */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon blue">
              <span role="img" aria-label="calendar">
                <Icon name="calendar" size={20} />
              </span>
            </div>
            <div>
              <h2 className="settings-card-title">Pay Schedule</h2>
              <p className="settings-card-description">
                Semi-monthly cutoffs — each pay period runs from its start day
                to the day before the next one, wrapping across the month
              </p>
            </div>
          </div>
        </div>

        <div className="settings-card-body">
          {cutoffLockedBy && (
            <div
              className="settings-input-hint"
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 8,
                background: 'var(--ui-accent-tint)',
                color: 'var(--ui-text-secondary)',
              }}
            >
              Cutoffs are locked while {cutoffLockedBy} exists — complete or
              discard it first. Changes apply to future periods only.
            </div>
          )}

          <div className="settings-form-grid-2">
            {[0, 1].map((idx) => (
              <div
                key={idx}
                className={`rate-card ${cutoffError ? 'error' : ''}`}
                onClick={(e) => {
                  if (!canEdit || cutoffLockedBy) return;
                  const input = e.currentTarget.querySelector('input');
                  if (input) input.focus();
                }}
              >
                <div className="rate-card-header">
                  <span className="rate-card-label">
                    {idx === 0 ? 'First cutoff' : 'Second cutoff'}
                  </span>
                </div>
                <div className="rate-card-value">
                  <input
                    type="number"
                    className={`rate-card-input ${cutoffError ? 'error' : ''}`}
                    aria-label={idx === 0 ? 'First cutoff start day' : 'Second cutoff start day'}
                    value={cutoffDays[idx]}
                    onChange={(e) => handleCutoffChange(idx, e.target.value)}
                    min="1"
                    max="31"
                    step="1"
                    disabled={!canEdit || !!cutoffLockedBy}
                    readOnly={!canEdit || !!cutoffLockedBy}
                  />
                  <span className="rate-card-suffix">of the month</span>
                </div>
                <div className="rate-card-description">
                  {idx === 0
                    ? 'First pay period starts on this day'
                    : 'Second pay period starts on this day'}
                </div>
              </div>
            ))}
          </div>

          {cutoffError ? (
            <div
              className="settings-input-hint"
              style={{ color: 'var(--ui-danger)', marginTop: 8 }}
            >
              {cutoffError}
            </div>
          ) : (
            <div className="settings-input-hint" style={{ marginTop: 8 }}>
              {(() => {
                const preview = officialPeriods(cutoffDays, toYMDString(new Date()), {
                  monthsBack: 0,
                  monthsForward: 0,
                });
                return `This month's periods: ${preview
                  .map((p) => `${p.start} → ${p.end}`)
                  .join('  ·  ')}. Days past a month's end clamp to its last day.`;
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Payroll Approval Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon">
              <span role="img" aria-label="approval"><Icon name="check-circle" size={16} /></span>
            </div>
            <div>
              <h2 className="settings-card-title">Payroll Approval</h2>
              <p className="settings-card-description">
                Require a second pair of eyes before payroll is finalized
              </p>
            </div>
          </div>
        </div>

        <div className="settings-card-body">
          <div className="settings-info-box">
            <span className="settings-info-box-icon"><Icon name="info" size={16} /></span>
            <span className="settings-info-box-text">
              Every payroll run goes through Submit → Approve. Choose whether
              the submitter may approve their own run.
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {[
              {
                value: 'self-approval',
                label: 'Self-approval',
                help: 'The submitter can approve or reject their own payroll run.',
              },
              {
                value: 'maker-checker',
                label: 'Maker-checker',
                help: 'The submitter cannot approve or reject their own payroll run — a different person must.',
              },
            ].map(opt => {
              const active = rates.payrollApprovalType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setRates({ ...rates, payrollApprovalType: opt.value })}
                  style={{
                    flex: 1,
                    textAlign: 'left',
                    padding: '12px 16px',
                    borderRadius: 8,
                    border: `2px solid ${active ? 'var(--ui-primary)' : 'var(--ui-border)'}`,
                    background: active ? 'var(--ui-primary-tint)' : 'var(--ui-surface)',
                    cursor: canEdit ? 'pointer' : 'not-allowed',
                    color: 'var(--ui-text)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {opt.label}
                    {opt.value === 'self-approval' && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ui-text-muted)', fontWeight: 400 }}>
                        Default
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ui-text-secondary)', lineHeight: 1.4 }}>
                    {opt.help}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Floating Save Bar — hidden in read-only mode */}
      <div
        className={`settings-floating-save-bar${
          canEdit && (hasChanges() || saveSuccess) ? " visible" : ""
        }${saveSuccess ? " saved" : ""}`}
      >
        <div className="settings-save-bar-content">
          <div
            className={`settings-save-bar-left${
              saveSuccess ? " success" : ""
            }`}
          >
            <span className="settings-save-bar-dot"></span>
            <span className="settings-save-bar-check"><Icon name="check" size={14} /></span>
            <span className="settings-save-bar-text">
              {saveSuccess ? "Changes saved" : "Unsaved changes"}
            </span>
          </div>
          {!saveSuccess && (
            <div className="settings-save-bar-actions">
              <button
                className="settings-btn settings-btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Discard
              </button>
              <button
                className="settings-btn settings-btn-primary"
                onClick={handleSave}
                disabled={!hasChanges() || !isValid() || saving}
              >
                {saving ? (
                  <>
                    <span className="settings-btn-spinner"></span>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayrollRatesPage;
